// Pure logic for the no-ticket branch of "Receive a load" (BUILD_SPEC.md §8.1
// amendment R3 — ticket-less docket receipts for wax/packaging; timber keeps
// the ticketed path). Folded into ReceiveLoadWizard.tsx as an optional step
// by the 2026-08-25 consolidation (Stuart ruling: one receive entry point,
// not two screens). Deliberately has NO React/Mantine import so the
// count-line-vs-mass-line payload construction is unit-testable without
// rendering anything (goal G-WebA item 6).
import { isCountLine, type GoodsReceiptLineInput } from '@zebra/core';
import type { PurchaseOrderLineRow, ProductRow } from './api';

export interface ManualLineInput {
  /** Units for a count (EACH) line, kg for a mass line — same field either
   * way; which one it means follows the PO line's own mode. */
  qty: string;
  /** Only ever meaningful for a mass line; ignored for a count line. */
  mcPct: string;
}

export const EMPTY_MANUAL_LINE_INPUT: ManualLineInput = { qty: '', mcPct: '' };

export type ManualLineBuildResult = { line: GoodsReceiptLineInput } | { error: string } | null;

/**
 * Builds ONE goods-receipt line body, mode-aware per `isCountLine` (THE
 * shared mass/count discriminant, `@zebra/core` — never a local null check).
 * Returns:
 *   - `null` when nothing was entered for this line — a manual receipt need
 *     not cover every PO line at once, so a blank line is silently skipped,
 *     not an error.
 *   - `{ error }` when what's entered is invalid for the line's mode (not a
 *     positive number; a fractional quantity on an EACH line — mirrors
 *     schemas/purchasing.ts's qtyEachSchema / schemas/intake.ts's
 *     positiveEachQtySchema, whole-units-only; the server remains the actual
 *     enforcement point).
 *   - `{ line }` — the correct wire variant: a count line's body carries ONLY
 *     `{poLineId, productId, qty}` (no `mcPct`), a mass line's ONLY
 *     `{poLineId, productId, qtyKg, mcPct}` — matching the `.strict()`
 *     branches on both sides of `goodsReceiptLineInputSchema`'s union (a
 *     mixed-mode object would fail BOTH branches, not silently drop the extra
 *     key).
 */
export function buildManualLineInput(poLine: PurchaseOrderLineRow, input: ManualLineInput): ManualLineBuildResult {
  const qtyTrimmed = input.qty.trim();
  if (!qtyTrimmed) return null;

  const qtyNum = Number(qtyTrimmed);
  if (Number.isNaN(qtyNum) || qtyNum <= 0) {
    return { error: 'Quantity must be a positive number' };
  }

  if (isCountLine(poLine)) {
    if (!Number.isInteger(qtyNum)) {
      return { error: 'EACH lines must be received in whole units' };
    }
    return { line: { poLineId: poLine.id, productId: poLine.productId, qty: qtyTrimmed } };
  }

  return { line: { poLineId: poLine.id, productId: poLine.productId, qtyKg: qtyTrimmed, mcPct: input.mcPct.trim() || null } };
}

export interface ManualLinesBuild {
  lines: GoodsReceiptLineInput[];
  errors: Map<string, string>;
}

/** Aggregates `buildManualLineInput` over every PO line the operator touched.
 * `errors` is keyed by PO line id so the screen can show each bad line's own
 * message inline; `lines` only ever contains the successfully-built ones
 * (never a partial/invalid entry) — the caller gates submission on
 * `errors.size === 0 && lines.length > 0`, never on `lines` alone (an
 * all-blank PO would otherwise "succeed" with zero lines, which the server's
 * own `min(1)` would then reject anyway, but the UI should catch it first
 * with a clearer message than a round-trip 400). */
export function buildManualLines(poLines: readonly PurchaseOrderLineRow[], inputs: Record<string, ManualLineInput>): ManualLinesBuild {
  const lines: GoodsReceiptLineInput[] = [];
  const errors = new Map<string, string>();
  for (const poLine of poLines) {
    const result = buildManualLineInput(poLine, inputs[poLine.id] ?? EMPTY_MANUAL_LINE_INPUT);
    if (result === null) continue;
    if ('error' in result) {
      errors.set(poLine.id, result.error);
    } else {
      lines.push(result.line);
    }
  }
  return { lines, errors };
}

/** Whether the moisture field should be shown for a mass PO line in the
 * no-ticket docket entry — the product is moisture-tracked (BUILD_SPEC.md §9.2 record-keeping,
 * independent of pricing) OR the line's own pricing basis actually requires a
 * reading to compute payable (BDT/MOISTURE_BAND) — the union of both, so the
 * field is never hidden in a case where posting would otherwise 422
 * MISSING_MOISTURE. Always false for a count (EACH) line — no moisture basis
 * exists there at all. */
export function showMoistureField(poLine: PurchaseOrderLineRow, product: ProductRow | undefined): boolean {
  if (isCountLine(poLine)) return false;
  return Boolean(product?.isMoistureTracked) || poLine.pricingBasis !== 'FLAT_WET';
}
